import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  memo,
} from "react";
import { listAAAFoodHandlerSessions } from "../graphql/queries";
import { API, graphqlOperation } from "aws-amplify";
import {
  Box,
  Button,
  Text,
  TextInput,
  Notification,
  Layer,
} from "grommet";
import {
  UpdateAAAFoodHandlerSession,
  grantAAAFoodHandlerAccess,
  revokeAAAFoodHandlerAccess,
} from "../graphql/mutations";
import { useUser } from "../AuthContext";
import pinIcon from "../assets/icons/pin.svg";
import unlockIcon from "../assets/icons/unlock.svg";
import lockIcon from "../assets/icons/lock.svg";
import checkIcon from "../assets/icons/check.svg";
import searchIcon from "../assets/icons/search.svg";

const PAGE_LIMIT = 300;
const WIDE_LAYOUT_MIN_WIDTH = 1050;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULTS_LIMIT = 100;
const PINBOARD_STORAGE_PREFIX = "aaa-food-handlers-pinboard:";
const ACTION_ICON_SIZE = "17px";
const ACTION_BUTTON_CLASS = "aaa-fh-action-btn";
const FINISH_BUTTON_COLOR = "#b92b37";
const ACTION_BUTTON_STYLE = {
  height: "38px",
  minHeight: "38px",
  paddingLeft: "10px",
  paddingRight: "10px",
  boxSizing: "border-box",
  maxWidth: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  transition: "box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
  backgroundColor: "#FFFFFF",
  color: "#333333",
  border: "1px solid rgba(0, 0, 0, 0.12)",
};

const ACTION_BUTTON_SHADOW_CSS = `
.${ACTION_BUTTON_CLASS} {
  background-color: #ffffff !important;
  color: #333333 !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.14);
  transition: box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}
.${ACTION_BUTTON_CLASS}:hover:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: #f7f7f7 !important;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.18);
}
.${ACTION_BUTTON_CLASS}:active:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: #efefef !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
}
.${ACTION_BUTTON_CLASS}:disabled,
.${ACTION_BUTTON_CLASS}[disabled],
.${ACTION_BUTTON_CLASS}[aria-disabled="true"] {
  background-color: #ffffff !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  opacity: 0.55;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--unlock-ready {
  background-color: rgb(229, 249, 242) !important;
  border-color: rgb(170, 214, 196) !important;
  color: #1b5e4a !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--unlock-ready:hover:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: rgb(210, 242, 230) !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--unlock-ready:active:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: rgb(195, 234, 220) !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--lock-ready {
  background-color: rgb(255, 236, 236) !important;
  border-color: rgb(230, 180, 180) !important;
  color: #8a2e2e !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--lock-ready:hover:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: rgb(255, 224, 224) !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--lock-ready:active:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: rgb(255, 214, 214) !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--finish {
  background-color: ${FINISH_BUTTON_COLOR} !important;
  border-color: ${FINISH_BUTTON_COLOR} !important;
  color: #ffffff !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--finish:hover:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: #a32530 !important;
  border-color: #a32530 !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--finish:active:not(:disabled):not([disabled]):not([aria-disabled="true"]) {
  background-color: #8f202a !important;
  border-color: #8f202a !important;
}
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--finish:disabled,
.${ACTION_BUTTON_CLASS}.${ACTION_BUTTON_CLASS}--finish[disabled] {
  background-color: ${FINISH_BUTTON_COLOR} !important;
  border-color: ${FINISH_BUTTON_COLOR} !important;
  color: #ffffff !important;
}
`;

function getActionButtonStyle(extraStyle) {
  return extraStyle
    ? { ...ACTION_BUTTON_STYLE, ...extraStyle }
    : ACTION_BUTTON_STYLE;
}

function getActionButtonClassName(...modifiers) {
  return [ACTION_BUTTON_CLASS, ...modifiers.filter(Boolean)].join(" ");
}

const CARD_LABEL_STYLE = {
  fontSize: "16px",
  lineHeight: "20px",
};

const CARD_VALUE_STYLE = {
  fontSize: "16px",
  lineHeight: "20px",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const CARD_NAME_STYLE = {
  fontSize: "17px",
  lineHeight: "21px",
  fontWeight: 600,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const CARD_LIST_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: 0,
  maxWidth: "100%",
  height: "auto",
  paddingRight: "6px",
};

const SCROLL_CONTAINER_STYLE = {
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
  padding: 0,
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable",
};

const SCROLL_INNER_STYLE = {
  paddingRight: "6px",
  minWidth: 0,
  maxWidth: "100%",
};

const CARD_SHELL_STYLE = {
  minWidth: 0,
  maxWidth: "100%",
  height: "auto",
  flex: "0 0 auto",
  flexShrink: 0,
  alignSelf: "stretch",
  backgroundColor: "rgb(200 224 228 / 52%)",
  boxShadow: "none",
};

const CARD_LIST_ITEM_STYLE = {
  minWidth: 0,
  maxWidth: "100%",
  height: "auto",
  flex: "0 0 auto",
  flexShrink: 0,
  alignSelf: "stretch",
};

const SEARCH_ICON_SIZE = "22px";
const SEARCH_PLACEHOLDER_COLOR = "#AAAAAA";
const STATUS_INDICATOR_SIZE = "36px";

const searchFieldIcon = (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: SEARCH_ICON_SIZE,
      height: SEARCH_ICON_SIZE,
      minWidth: SEARCH_ICON_SIZE,
      minHeight: SEARCH_ICON_SIZE,
      flexShrink: 0,
      backgroundColor: SEARCH_PLACEHOLDER_COLOR,
      WebkitMaskImage: `url(${searchIcon})`,
      maskImage: `url(${searchIcon})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
      pointerEvents: "none",
    }}
  />
);

function ExamStatusIndicator({ locked }) {
  const label = locked ? "Exam is locked" : "Exam is unlocked";
  const iconSrc = locked ? lockIcon : unlockIcon;
  const maskColor = locked ? "#b92b37" : "rgb(33 149 114)";

  return (
    <Box
      as="span"
      role="img"
      tabIndex={0}
      aria-label={label}
      flex={false}
      style={{
        display: "inline-block",
        width: STATUS_INDICATOR_SIZE,
        height: STATUS_INDICATOR_SIZE,
        minWidth: STATUS_INDICATOR_SIZE,
        minHeight: STATUS_INDICATOR_SIZE,
        maxWidth: STATUS_INDICATOR_SIZE,
        maxHeight: STATUS_INDICATOR_SIZE,
        flexShrink: 0,
        alignSelf: "flex-start",
        padding: 0,
        border: "none",
        backgroundColor: maskColor,
        WebkitMaskImage: `url(${iconSrc})`,
        maskImage: `url(${iconSrc})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        outlineOffset: "2px",
        cursor: "default",
      }}
    />
  );
}

const QUEUE_FILTER = {
  or: [
    { Status: { eq: "QUEUE" } },
    { Status: { attributeExists: false } },
  ],
};

function mergeBySessionId(existing, incoming) {
  const map = new Map();
  existing.forEach((session) => {
    if (session?.SessionId != null) {
      map.set(session.SessionId, session);
    }
  });
  incoming.forEach((session) => {
    if (session?.SessionId != null) {
      map.set(session.SessionId, session);
    }
  });
  return Array.from(map.values());
}

function upsertSessionById(list, session) {
  if (!session?.SessionId) {
    return list;
  }
  const exists = list.some((item) => item.SessionId === session.SessionId);
  if (!exists) {
    return [...list, session];
  }
  return list.map((item) =>
    item.SessionId === session.SessionId ? { ...item, ...session } : item
  );
}

function patchSessionById(list, sessionId, patch) {
  return list.map((item) =>
    item.SessionId === sessionId ? { ...item, ...patch } : item
  );
}

function isQueueSession(session) {
  return session?.Status === "QUEUE" || session?.Status == null;
}

function getProctorId(user) {
  const email = user?.attributes?.email;
  if (typeof email === "string" && email.trim()) {
    return email.trim();
  }
  const username = user?.username;
  if (typeof username === "string" && username.trim()) {
    return username.trim();
  }
  try {
    const raw = sessionStorage.getItem("user");
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw);
    const storedEmail = stored?.attributes?.email;
    if (typeof storedEmail === "string" && storedEmail.trim()) {
      return storedEmail.trim();
    }
    if (typeof stored?.username === "string" && stored.username.trim()) {
      return stored.username.trim();
    }
  } catch (error) {
    return null;
  }
  return null;
}

function readPinboardFromStorage(proctorId) {
  if (!proctorId) {
    return [];
  }
  try {
    const raw = localStorage.getItem(`${PINBOARD_STORAGE_PREFIX}${proctorId}`);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const map = new Map();
    parsed.forEach((session) => {
      if (session && session.SessionId != null) {
        map.set(session.SessionId, session);
      }
    });
    return Array.from(map.values());
  } catch (error) {
    return [];
  }
}

function writePinboardToStorage(proctorId, pinboard) {
  if (!proctorId) {
    return;
  }
  try {
    localStorage.setItem(
      `${PINBOARD_STORAGE_PREFIX}${proctorId}`,
      JSON.stringify(pinboard)
    );
  } catch (error) {
    // Ignore storage quota / private-mode failures.
  }
}

async function listSessions({ filter, nextToken } = {}) {
  const variables = {
    limit: PAGE_LIMIT,
  };
  if (filter != null) {
    variables.filter = filter;
  }
  if (nextToken) {
    variables.nextToken = nextToken;
  }

  const data = await API.graphql({
    query: listAAAFoodHandlerSessions,
    variables,
  });

  return data.data.listAAAFoodHandlerSessions;
}

function useWideLayout(minWidth = WIDE_LAYOUT_MIN_WIDTH) {
  const [isWide, setIsWide] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
    const handleChange = (event) => {
      setIsWide(event.matches);
    };
    setIsWide(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [minWidth]);

  return isWide;
}

const createMaskedIcon = (iconSrc, backgroundColor) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: ACTION_ICON_SIZE,
      height: ACTION_ICON_SIZE,
      flexShrink: 0,
      backgroundColor,
      WebkitMaskImage: `url(${iconSrc})`,
      maskImage: `url(${iconSrc})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
    }}
  />
);

const pinActionIcon = (
  <img
    src={pinIcon}
    alt=""
    aria-hidden="true"
    style={{
      width: ACTION_ICON_SIZE,
      height: ACTION_ICON_SIZE,
      display: "block",
      flexShrink: 0,
    }}
  />
);
const finishActionIcon = createMaskedIcon(checkIcon, "#FFFFFF");
const unlockIconDefault = createMaskedIcon(unlockIcon, "#333333");
const unlockIconReady = createMaskedIcon(unlockIcon, "#1B5E4A");
const lockIconDefault = createMaskedIcon(lockIcon, "#333333");
const lockIconReady = createMaskedIcon(lockIcon, "#8A2E2E");

function AAAFoodHandlers() {
  const auth = useUser();
  const isWide = useWideLayout();
  const proctorId = useMemo(() => getProctorId(auth?.user), [auth?.user]);

  const [sessions, setSessions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMatchTotal, setSearchMatchTotal] = useState(0);
  const [pinboard, setPinboard] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [queueLoading, setQueueLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [pinConfirmSession, setPinConfirmSession] = useState(null);
  const [pinboardNotice, setPinboardNotice] = useState(null);

  const isMountedRef = useRef(true);
  const searchGenRef = useRef(0);
  const sessionsLoadGenRef = useRef(0);
  const pinboardHydratedForRef = useRef(null);
  const skipPinboardPersistRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      searchGenRef.current += 1;
      sessionsLoadGenRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!proctorId) {
      pinboardHydratedForRef.current = null;
      setPinboard([]);
      return;
    }
    if (pinboardHydratedForRef.current === proctorId) {
      return;
    }
    pinboardHydratedForRef.current = proctorId;
    skipPinboardPersistRef.current = true;
    setPinboard(readPinboardFromStorage(proctorId));
  }, [proctorId]);

  useEffect(() => {
    if (!proctorId || pinboardHydratedForRef.current !== proctorId) {
      return;
    }
    if (skipPinboardPersistRef.current) {
      skipPinboardPersistRef.current = false;
      return;
    }
    writePinboardToStorage(proctorId, pinboard);
  }, [proctorId, pinboard]);

  const loadSessions = useCallback(async () => {
    const loadId = ++sessionsLoadGenRef.current;
    setQueueLoading(true);
    setQueueError(null);

    let nextToken = null;
    let accumulated = [];

    try {
      do {
        const page = await listSessions({
          filter: QUEUE_FILTER,
          nextToken,
        });

        if (!isMountedRef.current || loadId !== sessionsLoadGenRef.current) {
          return;
        }

        accumulated = mergeBySessionId(accumulated, page.items || []);
        setSessions(accumulated);
        nextToken = page.nextToken || null;
      } while (nextToken);
    } catch (error) {
      if (!isMountedRef.current || loadId !== sessionsLoadGenRef.current) {
        return;
      }
      setQueueError("No matches found, try again");
    } finally {
      if (isMountedRef.current && loadId === sessionsLoadGenRef.current) {
        setQueueLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const searchIndex = useMemo(() => {
    const searchableSessions = mergeBySessionId(sessions, pinboard);
    return searchableSessions.map((session) => ({
      session,
      name:
        typeof session.Name === "string" ? session.Name.toLowerCase() : "",
      email:
        typeof session.EmailAddress === "string"
          ? session.EmailAddress.toLowerCase()
          : "",
    }));
  }, [sessions, pinboard]);

  useEffect(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (normalizedTerm.length < 3) {
      searchGenRef.current += 1;
      setSearchResults([]);
      setSearchMatchTotal(0);
      setSearching(false);
      return undefined;
    }

    setSearching(true);

    const debounceTimer = setTimeout(() => {
      const gen = ++searchGenRef.current;
      const matches = [];

      for (let i = 0; i < searchIndex.length; i += 1) {
        const entry = searchIndex[i];
        if (
          entry.name.includes(normalizedTerm) ||
          entry.email.includes(normalizedTerm)
        ) {
          matches.push(entry.session);
        }
      }

      if (!isMountedRef.current || gen !== searchGenRef.current) {
        return;
      }

      setSearchMatchTotal(matches.length);
      setSearchResults(matches.slice(0, SEARCH_RESULTS_LIMIT));
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimer);
      searchGenRef.current += 1;
    };
  }, [searchTerm, searchIndex]);

  const addOrUpdatePinboard = useCallback((session) => {
    setPinboard((prev) => upsertSessionById(prev, session));
  }, []);

  const handlePinRequest = useCallback((session) => {
    if (!session?.SessionId) {
      return;
    }
    setPinboard((prev) => {
      if (prev.some((item) => item.SessionId === session.SessionId)) {
        return prev;
      }
      if (session.ExamStatus === "UNLOCKED") {
        setPinConfirmSession(session);
        return prev;
      }
      return [...prev, session];
    });
  }, []);

  const confirmPinUnlocked = useCallback(() => {
    if (!pinConfirmSession) {
      return;
    }
    addOrUpdatePinboard(pinConfirmSession);
    setPinConfirmSession(null);
  }, [addOrUpdatePinboard, pinConfirmSession]);

  const cancelPinConfirm = useCallback(() => {
    setPinConfirmSession(null);
  }, []);

  const handleRemoveFromPinboard = useCallback((session) => {
    if (!session?.SessionId) {
      return;
    }
    if (session.ExamStatus === "UNLOCKED") {
      setPinboardNotice("Lock this exam before removing it from the pinboard.");
      return;
    }
    setPinboard((prev) =>
      prev.filter((item) => item.SessionId !== session.SessionId)
    );
  }, []);

  const handleUnlockSuccess = useCallback(
    (session) => {
      const updated = { ...session, ExamStatus: "UNLOCKED" };
      setSessions((prev) =>
        patchSessionById(prev, session.SessionId, updated)
      );
      setSearchResults((prev) =>
        patchSessionById(prev, session.SessionId, updated)
      );
      addOrUpdatePinboard(updated);
    },
    [addOrUpdatePinboard]
  );

  const handleLockSuccess = useCallback((session) => {
    const updated = { ...session, ExamStatus: "LOCKED" };
    setSessions((prev) => patchSessionById(prev, session.SessionId, updated));
    setSearchResults((prev) =>
      patchSessionById(prev, session.SessionId, updated)
    );
    setPinboard((prev) => patchSessionById(prev, session.SessionId, updated));
  }, []);

  const handleSessionFinished = useCallback((sessionId) => {
    setSessions((prev) =>
      patchSessionById(prev, sessionId, {
        Status: "COMPLETED",
        ExamStatus: "LOCKED",
      })
    );
    setSearchResults((prev) =>
      prev.map((session) =>
        session.SessionId === sessionId
          ? { ...session, Status: "COMPLETED", ExamStatus: "LOCKED" }
          : session
      )
    );
    setPinboard((prev) =>
      prev.filter((session) => session.SessionId !== sessionId)
    );
  }, []);

  const trimmedSearch = searchTerm.trim();
  const isActiveSearch = trimmedSearch.length >= 3;

  const queue = useMemo(
    () => sessions.filter(isQueueSession),
    [sessions]
  );

  const pinnedIds = useMemo(
    () => new Set(pinboard.map((session) => session.SessionId)),
    [pinboard]
  );

  const visibleQueue = useMemo(
    () => queue.filter((session) => !pinnedIds.has(session.SessionId)),
    [queue, pinnedIds]
  );

  const visibleSearchResults = useMemo(
    () => searchResults.filter((session) => !pinnedIds.has(session.SessionId)),
    [searchResults, pinnedIds]
  );

  const leftColumnTitle = isActiveSearch ? "SEARCH RESULTS" : "QUEUE";

  const renderSessionCardShell = (session, cardProps) => (
    <Box
      key={session.SessionId}
      round
      fill="horizontal"
      align="stretch"
      justify="start"
      style={CARD_SHELL_STYLE}
    >
      <SessionCard session={session} {...cardProps} />
    </Box>
  );

  const renderLeftColumnBody = () => {
    if (isActiveSearch) {
      if (visibleSearchResults.length > 0) {
        return (
          <Box style={CARD_LIST_STYLE}>
            {searchMatchTotal > SEARCH_RESULTS_LIMIT && (
              <Box pad="small" style={CARD_LIST_ITEM_STYLE}>
                <Text size="small" textAlign="center">
                  Showing {SEARCH_RESULTS_LIMIT} of {searchMatchTotal} matches.
                  Refine your search to narrow the results.
                </Text>
              </Box>
            )}
            {visibleSearchResults.map((session) =>
              renderSessionCardShell(session, {
                onFinished: handleSessionFinished,
                onUnlockSuccess: handleUnlockSuccess,
                onLockSuccess: handleLockSuccess,
                onPinRequest: handlePinRequest,
                showPinButton: true,
              })
            )}
          </Box>
        );
      }
      if (searching) {
        return (
          <Box align="center" justify="center" pad="medium">
            <Text size="medium" textAlign="center">
              Searching...
            </Text>
          </Box>
        );
      }
      return (
        <Box align="center" justify="center" pad="medium">
          <Text size="medium" textAlign="center">
            No results found for "{searchTerm}"
          </Text>
        </Box>
      );
    }

    return (
      <Box style={CARD_LIST_STYLE}>
        {queueError && (
          <Box align="center" gap="small" pad="small" style={CARD_LIST_ITEM_STYLE}>
            <Notification
              title={queueError}
              status="critical"
              onClose={() => setQueueError(null)}
            />
            <Button label="Retry Queue Load" onClick={loadSessions} />
          </Box>
        )}
        {queueLoading && (
          <Box
            align="center"
            justify="center"
            pad="small"
            style={CARD_LIST_ITEM_STYLE}
          >
            <Text size="small" color="status-unknown">
              Loading queue...
            </Text>
          </Box>
        )}
        {visibleQueue.map((session) =>
          renderSessionCardShell(session, {
            onFinished: handleSessionFinished,
            onUnlockSuccess: handleUnlockSuccess,
            onLockSuccess: handleLockSuccess,
            onPinRequest: handlePinRequest,
            showPinButton: true,
          })
        )}
      </Box>
    );
  };

  return (
    <Box
      align="stretch"
      justify="start"
      fill="horizontal"
      pad="small"
      gap="medium"
      style={{
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        ...(isWide
          ? {
              height: "100dvh",
              overflow: "hidden",
            }
          : {
              minHeight: "100dvh",
            }),
      }}
    >
      <style>{ACTION_BUTTON_SHADOW_CSS}</style>
      <Box
        round
        fill="horizontal"
        align="center"
        justify="center"
        background="white"
        flex={false}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          flexShrink: 0,
          boxShadow: "none",
        }}
      >
        <Box
          align="center"
          justify="center"
          width={{ max: "100%" }}
          pad={{ horizontal: "small", vertical: "small" }}
          gap="xsmall"
          style={{ maxWidth: "100%" }}
        >
          <Text size="large" weight="bold">
            AAA Food Handlers
          </Text>
          <Text size="medium" textAlign="center">
            CLICK FINISH EXAM WHEN COMPLETED TO LOCK AND REMOVE FROM QUEUE
          </Text>
          <Box width="medium" style={{ maxWidth: "100%" }} margin={{ top: "xsmall" }}>
            <TextInput
              label="Search By Name or Email"
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email"
              value={searchTerm}
              icon={searchFieldIcon}
              reverse
            />
          </Box>
          {searching && (
            <Text size="small" color="status-unknown">
              Searching...
            </Text>
          )}
          {pinboardNotice && (
            <Notification
              title={pinboardNotice}
              status="warning"
              onClose={() => setPinboardNotice(null)}
            />
          )}
        </Box>
      </Box>

      <Box
        direction={isWide ? "row" : "column"}
        gap={isWide ? undefined : "medium"}
        align="stretch"
        fill="horizontal"
        style={{
          minWidth: 0,
          maxWidth: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: isWide ? "row" : "column",
          ...(isWide
            ? {
                overflow: "hidden",
                columnGap: "8px",
              }
            : {}),
        }}
      >
        <Box
          flex={{ grow: 1, shrink: 1 }}
          basis={isWide ? "1/2" : "auto"}
          gap="small"
          fill="horizontal"
          pad="none"
          round="small"
          style={{
            minWidth: 0,
            maxWidth: "100%",
            padding: 0,
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            ...(isWide
              ? {
                  flex: "1 1 50%",
                  height: "100%",
                }
              : {
                  maxHeight: "42dvh",
                  flex: "0 1 auto",
                }),
          }}
        >
          <Text
            size="large"
            weight="bold"
            textAlign="center"
            style={{ flexShrink: 0 }}
          >
            {leftColumnTitle}
          </Text>
          <Box fill="horizontal" style={SCROLL_CONTAINER_STYLE}>
            {renderLeftColumnBody()}
          </Box>
        </Box>

        <Box
          flex={{ grow: 1, shrink: 1 }}
          basis={isWide ? "1/2" : "auto"}
          gap="medium"
          fill="horizontal"
          pad="none"
          style={{
            minWidth: 0,
            maxWidth: "100%",
            borderRight: "none",
            padding: "20px",
            backgroundColor: "#eff1ef",
            borderRadius: "15px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            ...(isWide
              ? {
                  flex: "1 1 50%",
                  height: "100%",
                }
              : {
                  minHeight: "35dvh",
                  flex: "1 1 auto",
                }),
          }}
        >
          <Box
            direction="row"
            align="center"
            justify="center"
            gap="xsmall"
            wrap
            style={{ flexShrink: 0 }}
          >
            <img
              src={pinIcon}
              alt=""
              aria-hidden="true"
              style={{
                width: "24px",
                height: "24px",
                display: "block",
                flexShrink: 0,
              }}
            />
            <Text size="large" weight="bold" textAlign="center">
              AAA Pinboard
            </Text>
          </Box>
          <Box fill="horizontal" style={SCROLL_CONTAINER_STYLE}>
            {pinboard.length === 0 ? (
              <Box
                align="center"
                gap="small"
                pad="medium"
                style={SCROLL_INNER_STYLE}
              >
                <Text size="medium" weight="bold" textAlign="center">
                  AAA Pinboard is clean
                </Text>
                <Text size="small" textAlign="center">
                  No current candidates selected, start by looking for your
                  assigned candidate and either pin it or unlock the exam to add
                  it to this section.
                </Text>
              </Box>
            ) : (
              <Box style={CARD_LIST_STYLE}>
                {pinboard.map((session) =>
                  renderSessionCardShell(session, {
                    onFinished: handleSessionFinished,
                    onUnlockSuccess: handleUnlockSuccess,
                    onLockSuccess: handleLockSuccess,
                    onRemoveFromPinboard: handleRemoveFromPinboard,
                    inPinboard: true,
                  })
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {pinConfirmSession && (
        <Layer
          position="center"
          modal
          onEsc={cancelPinConfirm}
          onClickOutside={cancelPinConfirm}
        >
          <Box pad="medium" gap="medium" width="medium" style={{ maxWidth: "90vw" }}>
            <Text weight="bold" size="large">
              Warning
            </Text>
            <Text>
              This exam status is unlocked. Make sure it was unlocked by you
              before pinning it to the pinboard. You will not be able to remove
              it until the exam is locked or finished.
            </Text>
            <Box direction="row" gap="small" justify="end" wrap>
              <Button
                label="Go Back"
                className={getActionButtonClassName()}
                style={getActionButtonStyle()}
                onClick={cancelPinConfirm}
              />
              <Button
                label="Pin"
                className={getActionButtonClassName()}
                style={getActionButtonStyle()}
                onClick={confirmPinUnlocked}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
}

export default AAAFoodHandlers;

const SessionCard = memo(function SessionCard({
  session,
  onFinished,
  onUnlockSuccess,
  onLockSuccess,
  onPinRequest,
  onRemoveFromPinboard,
  showPinButton = false,
  inPinboard = false,
}) {
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const [lockSuccess, setLockSuccess] = useState(false);
  const [finishSuccess, setFinishSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (session.ExamStatus === "LOCKED" || session.ExamStatus === null) {
      setLockSuccess(true);
    }
    if (session.ExamStatus === "UNLOCKED") {
      setUnlockSuccess(true);
    }
    if (session.Status === "COMPLETED") {
      setFinishSuccess(true);
    }
  }, []);

  async function lockExam() {
    if (actionPending) {
      return;
    }
    setActionPending(true);
    setUnlockSuccess(false);
    try {
      const data = await API.graphql(
        graphqlOperation(revokeAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: "578f877f-205f-47c6-85e0-afe16172237c",
          },
        })
      );

      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "LOCKED",
          },
        },
      });

      if (
        data.data.revokeFoodHandlerAccess ===
        "{message=Student access has been revoked}"
      ) {
        setLockSuccess(true);
        if (onLockSuccess) {
          onLockSuccess({ ...session, ExamStatus: "LOCKED" });
        }
      } else {
        setMessage(data.data.revokeFoodHandlerAccess);
        setError(true);
        setLockSuccess(false);
        setUnlockSuccess(false);
      }
    } catch (error) {
      setError(true);
      setLockSuccess(false);
      setUnlockSuccess(false);
    } finally {
      setActionPending(false);
    }
  }

  async function unlockExam() {
    if (actionPending) {
      return;
    }
    setActionPending(true);
    setLockSuccess(false);
    try {
      const data = await API.graphql(
        graphqlOperation(grantAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: "578f877f-205f-47c6-85e0-afe16172237c",
          },
        })
      );

      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "UNLOCKED",
          },
        },
      });
      if (
        data.data.grantFoodHandlerAccess ===
        "{message=Student access has been granted successfully}"
      ) {
        setUnlockSuccess(true);
        if (onUnlockSuccess) {
          onUnlockSuccess({ ...session, ExamStatus: "UNLOCKED" });
        }
      } else {
        setMessage(data.data.grantFoodHandlerAccess);
        setError(true);
        setLockSuccess(false);
        setUnlockSuccess(false);
      }
    } catch (error) {
      setError(true);
      setLockSuccess(false);
      setUnlockSuccess(false);
    } finally {
      setActionPending(false);
    }
  }

  async function finishExam() {
    if (actionPending) {
      return;
    }
    setActionPending(true);
    setUnlockSuccess(false);
    try {
      const data = await API.graphql(
        graphqlOperation(revokeAAAFoodHandlerAccess, {
          body: {
            user_id: session.StudentId,
            course_id: session.ExamId,
            token: "578f877f-205f-47c6-85e0-afe16172237c",
          },
        })
      );

      await API.graphql({
        query: UpdateAAAFoodHandlerSession,
        variables: {
          input: {
            SessionId: session.SessionId,
            ExamStatus: "LOCKED",
            Status: "COMPLETED",
          },
        },
      });

      if (
        data.data.revokeFoodHandlerAccess ===
        "{message=Student access has been revoked}"
      ) {
        setLockSuccess(true);
        setFinishSuccess(true);
        if (onFinished) {
          onFinished(session.SessionId);
        }
      } else {
        setMessage(data.data.revokeFoodHandlerAccess);
        setError(true);
        setLockSuccess(false);
        setUnlockSuccess(false);
      }
    } catch (error) {
      setError(true);
      setLockSuccess(false);
      setUnlockSuccess(false);
    } finally {
      setActionPending(false);
    }
  }

  const isUnlocked = session.ExamStatus === "UNLOCKED";
  const isLockedStatus = session.ExamStatus === "LOCKED";
  const removeBlocked = inPinboard && isUnlocked;
  const unlockButtonIcon = isLockedStatus ? unlockIconReady : unlockIconDefault;
  const lockButtonIcon = isUnlocked ? lockIconReady : lockIconDefault;

  function handleRemoveClick() {
    if (!onRemoveFromPinboard) {
      return;
    }
    onRemoveFromPinboard(session);
  }

  return (
    <Box
      align="stretch"
      justify="start"
      pad={{ horizontal: "xsmall", vertical: "xsmall" }}
      style={{
        minWidth: 0,
        height: "auto",
        flex: "0 0 auto",
        flexShrink: 0,
      }}
    >
      <Box
        pad={{ horizontal: "small", vertical: "small" }}
        gap="small"
        direction="row"
        wrap
        align="start"
        style={{ minWidth: 0 }}
      >
        {unlockSuccess && <ExamStatusIndicator locked={false} />}
        {lockSuccess && <ExamStatusIndicator locked />}
        {error && (
          <Notification
            title="Something may have gone wrong. Please try again."
            message={message}
            status="critical"
            onClose={() => setError(false)}
          />
        )}
        {finishSuccess && (
          <Notification title="Exam Completed" status="normal" />
        )}

        <Box
          direction="column"
          gap="xsmall"
          align="start"
          style={{ minWidth: 0, flex: "1 1 200px", maxWidth: "100%" }}
        >
          <Box direction="row" gap="xsmall" wrap align="baseline">
            <Text weight="bold" style={CARD_LABEL_STYLE}>
              Tester Name:
            </Text>
            <Text style={CARD_NAME_STYLE}>{session.Name}</Text>
          </Box>
          <Box direction="row" gap="xsmall" wrap align="baseline">
            <Text weight="bold" style={CARD_LABEL_STYLE}>
              Email:
            </Text>
            <Text style={CARD_VALUE_STYLE}>{session.EmailAddress}</Text>
          </Box>
          <Box direction="row" gap="xsmall" wrap align="baseline">
            <Text weight="bold" style={CARD_LABEL_STYLE}>
              Course Language:
            </Text>
            <Text style={CARD_VALUE_STYLE}>{session.CourseLanguage}</Text>
          </Box>
          <Box direction="row" gap="xsmall" wrap align="baseline">
            <Text weight="bold" style={CARD_LABEL_STYLE}>
              Session Date:
            </Text>
            <Text style={CARD_VALUE_STYLE}>
              {session.createdAt ? session.createdAt.split("T")[0] : ""}
            </Text>
          </Box>
        </Box>
        <Box
          direction="column"
          gap="xsmall"
          style={{ flex: "0 0 auto", maxWidth: "100%" }}
        >
          {showPinButton && !inPinboard && (
            <Button
              icon={pinActionIcon}
              label="Pin"
              gap="small"
              onClick={() => onPinRequest && onPinRequest(session)}
              a11yTitle="Stick to pinboard"
              aria-label="Stick to pinboard"
              className={getActionButtonClassName()}
              style={getActionButtonStyle()}
            />
          )}
          <Button
            icon={unlockButtonIcon}
            label="Unlock Exam"
            gap="small"
            onClick={unlockExam}
            disabled={actionPending}
            a11yTitle="Unlock exam"
            aria-label="Unlock exam"
            className={getActionButtonClassName(
              isLockedStatus ? `${ACTION_BUTTON_CLASS}--unlock-ready` : null
            )}
            style={getActionButtonStyle()}
          />
          <Button
            icon={lockButtonIcon}
            label="Lock Exam"
            gap="small"
            onClick={lockExam}
            disabled={actionPending}
            a11yTitle="Lock exam"
            aria-label="Lock exam"
            className={getActionButtonClassName(
              isUnlocked ? `${ACTION_BUTTON_CLASS}--lock-ready` : null
            )}
            style={getActionButtonStyle()}
          />
        </Box>
      </Box>
      <Box
        direction="column"
        gap="xsmall"
        width={{ max: "100%" }}
        margin={{ bottom: "xsmall" }}
        align="center"
      >
        <Button
          icon={finishActionIcon}
          label="Finish Exam"
          gap="small"
          onClick={finishExam}
          disabled={actionPending}
          a11yTitle="Finish exam"
          aria-label="Finish exam"
          className={getActionButtonClassName(`${ACTION_BUTTON_CLASS}--finish`)}
          style={getActionButtonStyle()}
        />
        {inPinboard && (
          <Button
            label="Remove from pinboard"
            onClick={handleRemoveClick}
            a11yTitle="Remove from pinboard"
            aria-label="Remove from pinboard"
            aria-disabled={removeBlocked ? "true" : undefined}
            className={getActionButtonClassName()}
            style={getActionButtonStyle(
              removeBlocked
                ? { opacity: 0.5, cursor: "not-allowed" }
                : undefined
            )}
          />
        )}
      </Box>
    </Box>
  );
});
